import { useCameraGroupDetails } from '@hakimo-ui/hakimo/data-access';
import { LoadingIndicator } from '@hakimo-ui/hakimo/ui-elements';
import { useCanUpdateCameraGroup } from '@hakimo-ui/hakimo/util';
import { Alert, Button } from '@hakimo-ui/shared/ui-base';
import { useCallback, useEffect, useState } from 'react';
import CameraGroupHeader from './CameraGroupHeader';
import CameraGroupOverview from './CameraGroupOverview';
import UpdateCameraGroup from './UpdateCameraGroup';

interface Props {
  cameraGroupId: string;
  onDeleted?: () => void;
}

function CameraGroupDetails({ cameraGroupId, onDeleted }: Props) {
  const {
    data: group,
    isFetching,
    isError,
    error,
  } = useCameraGroupDetails(cameraGroupId);

  const [isEditMode, setIsEditMode] = useState(false);

  const canUpdateCameraGroup = useCanUpdateCameraGroup();

  const exitEditMode = useCallback(() => {
    setIsEditMode(false);
  }, []);

  useEffect(() => {
    setIsEditMode(false);
  }, [cameraGroupId]);

  if (isFetching && !group) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="m-6">
        <Alert type="error">{error.message}</Alert>
      </div>
    );
  }

  if (!group) return null;

  if (isEditMode && canUpdateCameraGroup) {
    return <UpdateCameraGroup group={group} onClose={exitEditMode} />;
  }

  return (
    <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg min-h-screen">
      <CameraGroupHeader
        title={group.name}
        subtitle={group.locationName}
        actions={
          canUpdateCameraGroup && (
            <Button variant="outline" onClick={() => setIsEditMode(true)}>
              Edit
            </Button>
          )
        }
      />

      <div className="mx-auto w-[82%] py-6">
        <CameraGroupOverview group={group} onDeleted={onDeleted} />
      </div>
    </div>
  );
}

export default CameraGroupDetails;
